import React, { useState, useRef, useEffect, useCallback } from "react";
import "../../Styles/Mp3Table.css";
import { useNavigate } from "react-router-dom";
import { mp3API, fileAPI } from "../../api/index"; // adjust path to match your project
import { resolveMediaUrl } from "../../utils/mediaUrl"; // adjust path to match your project

const COVER_COLORS = ["mp3123-cover-green", "mp3123-cover-blue", "mp3123-cover-orange"];

const formatTime = (seconds) => {
  if (!seconds || isNaN(seconds)) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

// Backend gives "YYYY-MM-DD" (Pydantic `date`). Display it the way the UI expects.
const formatDisplayDate = (isoDate) => {
  if (!isoDate) return "";
  const [year, month, day] = isoDate.split("-");
  if (!year || !month || !day) return isoDate;
  return `${day}-${month}-${year}`;
};

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const PAGE_SIZE = 10;

function Mp3Table() {
  const navigate = useNavigate();

  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [dateFilter, setDateFilter] = useState("All Dates");
  const [status, setStatus] = useState("All Status");
  const [currentPage, setCurrentPage] = useState(1);

  const [playingId, setPlayingId] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playError, setPlayError] = useState(""); // NEW: surfaces playback failures to the user

  const [showEditModal, setShowEditModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editFile, setEditFile] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const audioRefs = useRef({});

  // ---------- FETCH ----------
  const fetchSongs = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await mp3API.getAll({});
      const data = Array.isArray(res.data) ? res.data : res.data?.items || [];
      setSongs(
        data.map((s, i) => ({ ...s, color: COVER_COLORS[i % COVER_COLORS.length] }))
      );
    } catch (err) {
      console.error("Failed to load mp3 songs:", err);
      setError(err.response?.data?.detail || "Could not load MP3 songs.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSongs();
  }, [fetchSongs]);

  // Clean up all audio on unmount
  useEffect(() => {
    return () => {
      Object.values(audioRefs.current).forEach((audio) => {
        audio.pause();
        audio.src = "";
      });
    };
  }, []);

  const resetFilters = () => {
    setSearch("");
    setDateFilter("All Dates");
    setStatus("All Status");
    setCurrentPage(1);
  };

  // ---------- AUDIO PLAYBACK LOGIC ----------
  const getAudioEl = (id) => {
    if (!audioRefs.current[id]) {
      audioRefs.current[id] = new Audio();
    }
    return audioRefs.current[id];
  };

  const togglePlay = (item) => {
    setPlayError("");
    const resolvedUrl = resolveMediaUrl(item.file_url);

    if (!resolvedUrl) {
      setPlayError(`"${item.title}" has no playable file URL.`);
      return;
    }

    const audio = getAudioEl(item.id);

    // Pause any other currently playing song first
    if (playingId && playingId !== item.id) {
      const prevAudio = audioRefs.current[playingId];
      if (prevAudio) prevAudio.pause();
    }

    if (playingId === item.id) {
      audio.pause();
      setPlayingId(null);
      return;
    }

    if (audio.src !== resolvedUrl) {
      audio.src = resolvedUrl;
    }

    audio.play().catch((err) => {
      console.error("Audio playback failed:", err, "URL was:", resolvedUrl);
      setPlayError(
        `Could not play "${item.title}". The file may be missing on the server (URL: ${resolvedUrl}).`
      );
      setPlayingId(null);
    });

    audio.onloadedmetadata = () => setDuration(audio.duration);
    audio.ontimeupdate = () => setCurrentTime(audio.currentTime);
    audio.onended = () => {
      setPlayingId(null);
      setCurrentTime(0);
    };
    audio.onerror = () => {
      console.error("Audio element error for URL:", resolvedUrl);
      setPlayError(
        `Could not load "${item.title}". The file may be missing on the server.`
      );
      setPlayingId(null);
    };

    setPlayingId(item.id);
    setCurrentTime(0);

    // Fire-and-forget: bump the server-side play count. Don't block playback
    // on this, and don't fail loudly if it errors — it's just a counter.
    mp3API.incrementPlay(item.id).catch((err) =>
      console.warn("Play count increment failed:", err)
    );
    setSongs((prev) =>
      prev.map((s) => (s.id === item.id ? { ...s, play_count: (s.play_count || 0) + 1 } : s))
    );
  };

  // ---------- DOWNLOAD LOGIC ----------
  const handleDownload = async (item) => {
    const resolvedUrl = resolveMediaUrl(item.file_url);
    if (!resolvedUrl) {
      alert(`"${item.title}" has no downloadable file URL.`);
      return;
    }

    try {
      const response = await fetch(resolvedUrl);
      if (!response.ok) {
        throw new Error(`Server responded ${response.status}`);
      }
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = item.file_name || `${item.title}.mp3`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(blobUrl);

      await mp3API.incrementDownload(item.id);
      setSongs((prev) =>
        prev.map((s) =>
          s.id === item.id ? { ...s, download_count: (s.download_count || 0) + 1 } : s
        )
      );
    } catch (err) {
      console.error("Download failed:", err, "URL was:", resolvedUrl);
      alert(
        `Download failed for "${item.title}". The file may be missing on the server. Check the console for details.`
      );
    }
  };

  // ---------- SEARCH + FILTER (client-side) ----------
  const filteredSongs = songs.filter((item) => {
    const searchLower = search.trim().toLowerCase();
    const matchesSearch =
      searchLower === "" ||
      (item.title || "").toLowerCase().includes(searchLower) ||
      (item.file_name || "").toLowerCase().includes(searchLower);

    const matchesStatus =
      status === "All Status" || (item.status || "").toLowerCase() === status.toLowerCase();

    let matchesDate = true;
    if (item.date_added) {
      const [year, month] = item.date_added.split("-").map(Number);
      const today = new Date();
      if (dateFilter === "This Month") {
        matchesDate = month === today.getMonth() + 1 && year === today.getFullYear();
      } else if (dateFilter === "This Year") {
        matchesDate = year === today.getFullYear();
      }
    }

    return matchesSearch && matchesStatus && matchesDate;
  });

  const totalPages = Math.max(1, Math.ceil(filteredSongs.length / PAGE_SIZE));
  const pageItems = filteredSongs.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  // ---------- EDIT LOGIC ----------
  const openEditModal = (item) => {
    setEditItem({ ...item });
    setEditFile(null);
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditItem(null);
    setEditFile(null);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditItem((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditFileChange = (e) => {
    const selected = e.target.files[0];
    if (selected) setEditFile(selected);
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    setSavingEdit(true);
    try {
      let fileUrl = editItem.file_url;
      let fileName = editItem.file_name;
      let durationSeconds = editItem.duration_seconds;

      // Only re-upload if the user picked a replacement file
      if (editFile) {
        const uploadRes = await fileAPI.upload(editFile, "mp3");
        fileUrl =
          uploadRes.data?.url ||
          uploadRes.data?.file_url ||
          uploadRes.data?.photo_url ||
          fileUrl;
        fileName = editFile.name;

        try {
          const audio = new Audio();
          const objectUrl = URL.createObjectURL(editFile);
          durationSeconds = await new Promise((resolve, reject) => {
            audio.src = objectUrl;
            audio.onloadedmetadata = () => {
              URL.revokeObjectURL(objectUrl);
              resolve(Math.round(audio.duration));
            };
            audio.onerror = () => {
              URL.revokeObjectURL(objectUrl);
              reject(new Error("duration read failed"));
            };
          });
        } catch {
          // keep the old duration if re-reading fails
        }
      }

      await mp3API.update(editItem.id, {
        title: editItem.title,
        description: editItem.description || null,
        status: editItem.status,
        file_url: fileUrl,
        file_name: fileName,
        duration_seconds: durationSeconds,
      });

      await fetchSongs();
      closeEditModal();
    } catch (err) {
      console.error("Failed to update song:", err);
      alert(err.response?.data?.detail || "Something went wrong while saving changes.");
    } finally {
      setSavingEdit(false);
    }
  };

  // ---------- DELETE LOGIC ----------
  const openDeleteModal = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await mp3API.delete(deleteId);
      await fetchSongs();
      closeDeleteModal();
    } catch (err) {
      console.error("Failed to delete song:", err);
      alert(err.response?.data?.detail || "Could not delete this song.");
    } finally {
      setDeleting(false);
    }
  };

  // ---------- STATS ----------
  const totalSongs = songs.length;
  const totalPlays = songs.reduce((sum, s) => sum + (s.play_count || 0), 0);
  const totalDownloads = songs.reduce((sum, s) => sum + (s.download_count || 0), 0);

  const recentlyAdded = songs.filter((s) => {
    if (!s.date_added) return false;
    const today = new Date();
    const [year, month] = s.date_added.split("-").map(Number);
    return month === today.getMonth() + 1 && year === today.getFullYear();
  }).length;

  return (
    <div className="mp3123-page">
      {/* Header */}
      <div className="mp3123-header">
        <div className="mp3123-header-left">
          <div className="mp3123-page-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path d="M9 18V5l12-2v13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="6" cy="18" r="3" stroke="currentColor" strokeWidth="1.8" />
              <circle cx="18" cy="16" r="3" stroke="currentColor" strokeWidth="1.8" />
            </svg>
          </div>
          <div>
            <h2 className="mp3123-title">MP3 Songs</h2>
            <p className="mp3123-subtitle">Manage and organize all your MP3 songs</p>
          </div>
        </div>

        <button className="mp3123-add-btn" onClick={() => navigate("/dashboard/mp3-form")}>
          <span className="mp3123-add-icon">+</span> Add MP3 Song
        </button>
      </div>

      {error && <div className="mp3123-no-data">{error}</div>}
      {playError && <div className="mp3123-play-error">{playError}</div>}

      {/* Stat Cards */}
      <div className="mp3123-stats-grid">
        <div className="mp3123-stat-card">
          <div className="mp3123-stat-icon mp3123-stat-icon-green">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M9 18V5l12-2v13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="6" cy="18" r="3" stroke="currentColor" strokeWidth="1.8" />
              <circle cx="18" cy="16" r="3" stroke="currentColor" strokeWidth="1.8" />
            </svg>
          </div>
          <div className="mp3123-stat-info">
            <span className="mp3123-stat-label">Total Songs</span>
            <span className="mp3123-stat-value">{totalSongs}</span>
            <span className="mp3123-stat-sub">All time songs</span>
          </div>
        </div>

        <div className="mp3123-stat-card">
          <div className="mp3123-stat-icon mp3123-stat-icon-purple">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M4 15v-3a8 8 0 0116 0v3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              <rect x="2.5" y="15" width="4" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.8" />
              <rect x="17.5" y="15" width="4" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.8" />
            </svg>
          </div>
          <div className="mp3123-stat-info">
            <span className="mp3123-stat-label">Total Plays</span>
            <span className="mp3123-stat-value">{totalPlays.toLocaleString()}</span>
            <span className="mp3123-stat-sub">All time plays</span>
          </div>
        </div>

        <div className="mp3123-stat-card">
          <div className="mp3123-stat-icon mp3123-stat-icon-orange">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.8" />
              <path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </div>
          <div className="mp3123-stat-info">
            <span className="mp3123-stat-label">Recently Added</span>
            <span className="mp3123-stat-value">{recentlyAdded}</span>
            <span className="mp3123-stat-sub">This month</span>
          </div>
        </div>

        <div className="mp3123-stat-card">
          <div className="mp3123-stat-icon mp3123-stat-icon-blue">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M12 3v12m0 0l-4-4m4 4l4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </div>
          <div className="mp3123-stat-info">
            <span className="mp3123-stat-label">Total Downloads</span>
            <span className="mp3123-stat-value">{totalDownloads.toLocaleString()}</span>
            <span className="mp3123-stat-sub">All time downloads</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="mp3123-filters">
        <div className="mp3123-search-box">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
            <path d="M21 21l-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <input
            type="text"
            placeholder="Search by song title..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>

        <select
          className="mp3123-select"
          value={dateFilter}
          onChange={(e) => {
            setDateFilter(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option>All Dates</option>
          <option>This Month</option>
          <option>This Year</option>
        </select>

        <select
          className="mp3123-select"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option>All Status</option>
          <option>Active</option>
          <option>Inactive</option>
        </select>

        <button className="mp3123-reset-btn" onClick={resetFilters}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
            <path
              d="M4 4v6h6M20 20v-6h-6M4.5 15a8 8 0 0014.5 3.5M19.5 9A8 8 0 005 5.5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Reset Filters
        </button>
      </div>

      {/* Table */}
      <div className="mp3123-table-wrapper">
        <table className="mp3123-table  table table-hover">
          <thead>
            <tr>
              <th>S.No</th>
              <th>Song Title</th>
              <th>File Name</th>
              <th>Duration</th>
              <th>Date Added</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" className="mp3123-no-data">Loading...</td>
              </tr>
            ) : pageItems.length === 0 ? (
              <tr>
                <td colSpan="7" className="mp3123-no-data">No songs found.</td>
              </tr>
            ) : (
              pageItems.map((item, index) => {
                const isPlaying = playingId === item.id;
                const progressPercent = isPlaying && duration > 0 ? (currentTime / duration) * 100 : 0;

                return (
                  <tr key={item.id}>
                    <td>{(currentPage - 1) * PAGE_SIZE + index + 1}</td>

                    <td>
                      <div className="mp3123-song-cell">
                        <div className={`mp3123-cover ${item.color}`}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M9 18V5l12-2v13" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                            <circle cx="6" cy="18" r="3" stroke="#fff" strokeWidth="1.8" />
                            <circle cx="18" cy="16" r="3" stroke="#fff" strokeWidth="1.8" />
                          </svg>
                        </div>

                        <div className="mp3123-song-info">
                          <div className="mp3123-song-title">{item.title}</div>
                          <div className="mp3123-player">
                            <button className="mp3123-play-btn" onClick={() => togglePlay(item)}>
                              {isPlaying ? (
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                                  <rect x="5" y="4" width="5" height="16" rx="1" />
                                  <rect x="14" y="4" width="5" height="16" rx="1" />
                                </svg>
                              ) : (
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M6 4l14 8-14 8V4z" />
                                </svg>
                              )}
                            </button>

                            <span className="mp3123-player-time">
                              {isPlaying ? formatTime(currentTime) : "0:00"}
                            </span>

                            <div className="mp3123-progress-track">
                              <div className="mp3123-progress-fill" style={{ width: `${progressPercent}%` }} />
                            </div>

                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="mp3123-volume-icon">
                              <path d="M5 9v6h4l5 4V5L9 9H5z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
                            </svg>

                            <button
                              className="mp3123-download-btn"
                              onClick={() => handleDownload(item)}
                              title={`Download ${item.file_name}`}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                <path d="M12 3v12m0 0l-4.5-4.5M12 15l4.5-4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    </td>

                    <td>
                      <span className="mp3123-file-badge">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
                          <path d="M14 2v6h6" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
                        </svg>
                        {item.file_name}
                      </span>
                    </td>

                    <td>
                      <div className="mp3123-duration-cell">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
                          <path d="M12 7v5l3.5 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        {formatTime(item.duration_seconds)}
                      </div>
                    </td>

                    <td>
                      <div className="mp3123-date-cell">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                          <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.8" />
                          <path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                        </svg>
                        {formatDisplayDate(item.date_added)}
                      </div>
                    </td>

                    <td>
                      <span
                        className={`mp3123-status-badge ${
                          item.status === "active" ? "mp3123-status-active" : "mp3123-status-inactive"
                        }`}
                      >
                        {capitalize(item.status)}
                      </span>
                    </td>

                    <td>
                      <button className="mp3123-edit-btn" onClick={() => openEditModal(item)}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                          <path d="M12 20h9M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4 12.5-12.5z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        Edit
                      </button>

                      <button className="mp3123-delete-btn" onClick={() => openDeleteModal(item.id)}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                          <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0l-1 14a2 2 0 01-2 2H7a2 2 0 01-2-2L4 6h16z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="mp3123-pagination-bar">
        <span className="mp3123-pagination-info">
          Showing {pageItems.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1} to{" "}
          {(currentPage - 1) * PAGE_SIZE + pageItems.length} of {filteredSongs.length} songs
        </span>

        <div className="mp3123-pagination-controls">
          <button className="mp3123-page-arrow" onClick={() => handlePageChange(currentPage - 1)}>&lt;</button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              className={`mp3123-page-num ${currentPage === p ? "mp3123-active" : ""}`}
              onClick={() => handlePageChange(p)}
            >
              {p}
            </button>
          ))}

          <button className="mp3123-page-arrow" onClick={() => handlePageChange(currentPage + 1)}>&gt;</button>
        </div>
      </div>

      {/* ---------------- EDIT MODAL ---------------- */}
      {showEditModal && editItem && (
        <div className="mp3123-modal-overlay" onClick={closeEditModal}>
          <div className="mp3123-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mp3123-modal-header">
              <h3>Edit MP3 Song</h3>
              <button className="mp3123-modal-close" onClick={closeEditModal}>&times;</button>
            </div>

            <form className="mp3123-modal-body" onSubmit={handleEditSave}>
              <div className="mp3123-form-group">
                <label>Song Title</label>
                <input
                  type="text"
                  name="title"
                  value={editItem.title || ""}
                  onChange={handleEditChange}
                  required
                />
              </div>

              <div className="mp3123-form-group">
                <label>Replace File (optional)</label>
                <input type="file" accept=".mp3,audio/*" onChange={handleEditFileChange} />
                <span className="mp3123-current-file">Current: {editItem.file_name}</span>
              </div>

              <div className="mp3123-form-row">
                <div className="mp3123-form-group">
                  <label>Status</label>
                  <select name="status" value={editItem.status || "active"} onChange={handleEditChange}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>

                <div className="mp3123-form-group">
                  <label>Description</label>
                  <input
                    type="text"
                    name="description"
                    value={editItem.description || ""}
                    onChange={handleEditChange}
                  />
                </div>
              </div>

              <div className="mp3123-modal-footer">
                <button type="button" className="mp3123-btn-cancel" onClick={closeEditModal} disabled={savingEdit}>
                  Cancel
                </button>
                <button type="submit" className="mp3123-btn-save" disabled={savingEdit}>
                  {savingEdit ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ---------------- DELETE CONFIRM MODAL ---------------- */}
      {showDeleteModal && (
        <div className="mp3123-modal-overlay" onClick={closeDeleteModal}>
          <div className="mp3123-modal mp3123-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mp3123-confirm-icon">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0l-1 14a2 2 0 01-2 2H7a2 2 0 01-2-2L4 6h16z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>

            <h3 className="mp3123-confirm-title">Delete this song?</h3>
            <p className="mp3123-confirm-text">
              This action cannot be undone. Are you sure you want to delete this MP3 song?
            </p>

            <div className="mp3123-confirm-footer">
              <button className="mp3123-btn-cancel" onClick={closeDeleteModal} disabled={deleting}>
                Cancel
              </button>
              <button className="mp3123-btn-delete" onClick={confirmDelete} disabled={deleting}>
                {deleting ? "Deleting..." : "OK, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Mp3Table;